import React from "react";
import "./crawler.scss";
import CreateJob from "./createJob/createJob";
import JobList from "./pages/jobList/jobList";
import Button from "../components/button/button";
import Modal from "../components/modal/modal";
import {
    ActiveFilter,
    CrawlfishJob,
    JobListModalState,
    JobsDataState,
    Pagination,
} from "./crawler.interface";
import {
    _archiveJob,
    _deleteJob,
    _getAllJobs,
    _seeWorkers,
} from "./services/crawler.services";
import {
    CrawlfishJobDefaultState,
    JobListDefaultModalState,
    JobsDataDefaultFilters,
    JobsDataDefaultState,
    UserRoles,
} from "./crawler.models";
import { useSearchParams } from "react-router-dom";
import _ from "lodash";
import {
    __assignFilterStringJobsView,
    __serializeQueryParams,
    __translateFiltersToParamsJobView,
    __translateParamsToFilters,
    __validateFilterObjects,
} from "../utils/filters";
import UserContext, { UserContextInterface } from "../contexts/user-context";
import { SortInfo } from "./pages/jobDetail/jobDetail.interface";
import plLogo from "../../assets/images/performline-logo-color.png";
import { _logOut } from "./services/base.services";

export default function Crawler() {
    const [modalState, setModalState] = React.useState<JobListModalState>(
        JobListDefaultModalState
    );
    const [jobsData, setJobsData] =
        React.useState<JobsDataState>(JobsDataDefaultState);
    const [recreateJobData, setRecreateJobData] = React.useState<CrawlfishJob>(
        CrawlfishJobDefaultState
    );
    const [isRecreateMode, setIsRecreateMode] = React.useState<boolean>(false);

    const [searchParams, setSearchParams] = useSearchParams();
    const [activeWorkers, setActiveWorkers] = React.useState<any>({}); // eslint-disable-line @typescript-eslint/no-explicit-any

    const { user } = React.useContext<UserContextInterface>(UserContext);

    React.useEffect(() => {
        checkUrlParams();
    }, []);

    function checkUrlParams() {
        const serializedParams = __serializeQueryParams(searchParams);
        const paramsAsFilters = __translateParamsToFilters(serializedParams);

        if (
            serializedParams.length === 0 ||
            _.isEqual(paramsAsFilters, JobsDataDefaultFilters)
        ) {
            setJobsByFilters({ filters: JobsDataDefaultFilters });
        } else if (paramsAsFilters) {
            try {
                __validateFilterObjects(paramsAsFilters);
                setJobByUrlParams(paramsAsFilters);
            } catch (err) {
                setJobsData({
                    ...jobsData,
                    isFilterInProgress: false,
                    emptyResultsMessage: (
                        <span className='message filter-issue'>
                            There was a problem with the filter. Try clearing the filters via
                            &quot;Clear All&quot; button and trying again
                        </span>
                    ),
                });
                console.error(err);
            }
        }
    }

    function setUrlParams(
        filters: ActiveFilter[],
        pagination?: Pagination
    ): void {
        const params = __translateFiltersToParamsJobView(filters, pagination);
        setSearchParams(params);
    }

    function setJobByUrlParams(filtersFromURL: ActiveFilter[]): void {
        const offsetFilterIndex = filtersFromURL.findIndex(
            (filter: ActiveFilter) => filter.name === "page"
        );
        if (offsetFilterIndex > -1) {
            const offset = filtersFromURL[offsetFilterIndex];
            const paginationFromURL = { offset: Number(offset.value) };

            // remove the values "page" and "limit" because they aren't filters
            filtersFromURL.splice(offsetFilterIndex, 1);
            const limitFilterIndex = filtersFromURL.findIndex(
                (filter: ActiveFilter) => filter.name === "limit"
            );
            if (limitFilterIndex > -1) {
                filtersFromURL.splice(limitFilterIndex, 1);
            }

            setJobsByFilters({
                pagination: paginationFromURL,
                filters: filtersFromURL,
            });
        } else {
            setJobsByFilters({ filters: filtersFromURL });
        }
    }

    interface SetJobsByFiltersProps {
        pagination?: Pagination;
        filters: ActiveFilter[] | [];
        sortVal?: SortInfo;
    }

    async function setJobsByFilters({
        pagination,
        filters = [],
        sortVal = { sortIndex: 0, activeColumn: "" },
    }: SetJobsByFiltersProps): Promise<void> {
        setJobsData({ ...jobsData, isFilterInProgress: true });
        const filterString = __assignFilterStringJobsView(filters, pagination);
        setUrlParams(filters, pagination);

        try {
            const jobsResponse = await _getAllJobs(filterString);

            const allJobs = jobsResponse?.Results || [];

            if (allJobs.length == 0) {
                setJobsData({
                    ...jobsData,
                    ResultCount: jobsResponse?.ResultCount || {
                        Total: 0,
                        Current: 0,
                        Limit: 0,
                        Offset: 0,
                    },
                    loadedJobs: allJobs,
                    filteredJobs: allJobs,
                    activeFilters: filters,
                    emptyResultsMessage: (
                        <span className='message'>
                            No Jobs fomatching the filter option(s)
                        </span>
                    ),
                    isFilterInProgress: false,
                    sortInfo: {
                        sortIndex:
                            sortVal.sortIndex !== 0
                                ? sortVal.sortIndex
                                : jobsData.sortInfo.sortIndex,
                        activeColumn:
                            sortVal.activeColumn.length > 0
                                ? sortVal.activeColumn
                                : jobsData.sortInfo.activeColumn,
                    },
                });
            } else {
                if (sortVal.activeColumn === "") {
                    allJobs.sort((jobA: CrawlfishJob, jobB: CrawlfishJob) => {
                        return (
                            new Date(jobB.created_at).valueOf() -
                            new Date(jobA.created_at).valueOf()
                        );
                    });
                }
                setJobsData({
                    ...jobsData,
                    ResultCount: jobsResponse?.ResultCount || {
                        Total: 0,
                        Current: 0,
                        Limit: 0,
                        Offset: 0,
                    },
                    loadedJobs: allJobs,
                    filteredJobs: allJobs,
                    activeFilters: filters,
                    isFilterInProgress: false,
                    activePage: pagination?.offset || undefined,
                });
            }
        } catch (err) {
            console.log("Failed to get jobs. Error: ", err);
        }
    }

    function addJob(newJob: CrawlfishJob) {
        setModalState({ ...JobListDefaultModalState, isCreateJobModalOpen: true });
        addJobToUi(newJob)
    }

    function addJobToUi(newJob: CrawlfishJob) {
        const newFilteredJobs = jobsData.filteredJobs;
        newFilteredJobs.unshift(newJob);
        setJobsData({
            ...jobsData,
            loadedJobs: [...jobsData.loadedJobs, newJob],
            filteredJobs: newFilteredJobs,
        });
    }

    const recreateJobFromRow = React.useCallback((jobData: CrawlfishJob) => {
        setRecreateJobData(jobData);
        setIsRecreateMode(() => true)
        setModalState({ ...JobListDefaultModalState, isRecreateJobModalOpen: true });
    }, []);

    function createNewJob() {
        setJobsData({ ...jobsData });
        setModalState({ ...JobListDefaultModalState, isCreateJobModalOpen: true });
    }

    const deleteJob = React.useCallback(
        async (jobId: number) => {
            setJobsData({ ...jobsData, isFilterInProgress: true });
            await _deleteJob(jobId);
            const newFilteredJobs = [...jobsData.filteredJobs];
            const indexToRemove = jobsData.filteredJobs.findIndex(
                (job: CrawlfishJob) => job.id === jobId
            );
            newFilteredJobs.splice(indexToRemove, 1);
            setJobsData({
                ...jobsData,
                filteredJobs: newFilteredJobs,
                isFilterInProgress: false,
            });
        },
        [jobsData.filteredJobs]
    );

    const archiveJob = React.useCallback(
        async (jobId: number) => {
            await _archiveJob(jobId);
            const newFilteredJobs = [...jobsData.filteredJobs];
            const indexToRemove = jobsData.filteredJobs.findIndex(
                (job: CrawlfishJob) => job.id === jobId
            );
            newFilteredJobs.splice(indexToRemove, 1);
            setJobsData({ ...jobsData, filteredJobs: newFilteredJobs });
        },
        [jobsData.filteredJobs]
    );

    async function handleJobFilterSubmit(filter: ActiveFilter) {
        setJobsData({ ...jobsData, isFilterInProgress: true });
        const currentFilters = [...jobsData.activeFilters];
        currentFilters.forEach((currentFilter: ActiveFilter) => {
            if (currentFilter.name == filter.name) {
                const indexToRemove = currentFilters.findIndex(
                    (activeFilter: ActiveFilter) => activeFilter.name === filter.name
                );
                currentFilters.splice(indexToRemove, 1);
            }
        });
        setJobsByFilters({ filters: [...currentFilters, filter] });
    }

    function handleRemoveActiveFilter(filter: ActiveFilter) {
        const activeFilters = [...jobsData.activeFilters];
        const indexToRemove = activeFilters.findIndex(
            (activeFilter: ActiveFilter) =>
                activeFilter.name === filter.name && activeFilter.value == filter.value
        );
        activeFilters.splice(indexToRemove, 1);
        setJobsByFilters({ filters: activeFilters });
    }

    function clearActiveFilters() {
        setJobsByFilters({ filters: [] });
    }

    async function openWorkersModal() {
        if (user.current_role == UserRoles.Superuser) {
            const workers = await _seeWorkers(); // eslint-disable-line @typescript-eslint/no-explicit-any
            setActiveWorkers(workers);
            setModalState({ ...JobListDefaultModalState, isWorkersModalOpen: true });
        }
    }

    // Server Side Logic
    function setJobListPage(pageOffset: number): void {
        const paginationData = { offset: pageOffset };
        const activeFilters = [...jobsData.activeFilters];
        setUrlParams(activeFilters, paginationData);
        setJobsByFilters({
            pagination: paginationData,
            filters: activeFilters,
        });
    }

    const sortByColumnHeader = async (header: string) => {
        const sortIndex = (jobsData.sortInfo.sortIndex *= -1);
        const newSortFilter: ActiveFilter = {
            name: "sort",
            value: sortIndex == -1 ? `-${header}` : header,
        };

        const currentFilters = [...jobsData.activeFilters];
        currentFilters.forEach((currentFilter: ActiveFilter, index: number) => {
            // if sort exists, remove it
            if (currentFilter.name == "sort") {
                currentFilters.splice(index, 1);
            }
        });

        setJobsByFilters({
            filters: [...currentFilters, newSortFilter],
            sortVal: { sortIndex, activeColumn: header },
        });
    };

    const createNewJobForm = (
        <CreateJob
            addJob={addJob}
            isRecreateMode={isRecreateMode}
            existingJobData={recreateJobData}
            closeModal={() =>
                setModalState({
                    ...JobListDefaultModalState,
                    isCreateJobModalOpen: false,
                })
            }
        />
    );
    const recreateJobForm = (
        <CreateJob
            addJob={addJob}
            isRecreateMode={isRecreateMode}
            existingJobData={recreateJobData}
            closeModal={() =>
                setModalState({
                    ...JobListDefaultModalState,
                    isRecreateJobModalOpen: false,
                })}
        />
    );
    return (
        <>
            <div
                className='job-list-view-wrapper'
                data-testid='crawler'
                data-cy='crawler-main'
            >
                <div className='job-list-header'>
                    <div className='left-side-header'>
                        <h1>Crawlfish Jobs</h1>
                        <Button
                            cyTestId='create-job-button'
                            variant='button button--action button--solid button--small add-job'
                            onClick={() => createNewJob()}
                        >
                            + Job
                        </Button>
                    </div>
                    <img className='logo' src={plLogo} alt='Performline Logo' />
                    <div className='right-side-header'>
                        {user !== undefined && (
                            <>
                                {user.current_role == UserRoles.Superuser && (
                                    <a className='see-workers' onClick={() => openWorkersModal()}>
                                        Active Workers
                                    </a>
                                )}
                                <span>{user.email}</span>
                                <a className='logout-button' onClick={() => _logOut()}>
                                    {" "}
                                    Log out
                                </a>
                            </>
                        )}
                    </div>
                </div>
                {modalState.isCreateJobModalOpen && ( // =====> ?
                    <Modal
                        isOpen={modalState.isCreateJobModalOpen}
                        openModal={(toggleModal) =>
                            setModalState({
                                ...JobListDefaultModalState,
                                isCreateJobModalOpen: toggleModal,
                            })
                        }
                        title='+ Job'
                        key='new-job-modal'
                        className='create-job-modal'
                    >
                        {createNewJobForm}
                    </Modal>
                )}
                {modalState.isRecreateJobModalOpen && (
                    <Modal
                        isOpen={modalState.isRecreateJobModalOpen}
                        openModal={(toggleModal) =>
                            setModalState({
                                ...JobListDefaultModalState,
                                isCreateJobModalOpen: toggleModal,
                            })
                        }
                        title='Recreate Job'
                        key='recreate-job-modal'
                        className='create-job-modal'
                    >
                        {recreateJobForm}
                    </Modal>
                )}
                {modalState.isWorkersModalOpen && activeWorkers && (
                    <Modal
                        isOpen={modalState.isWorkersModalOpen}
                        openModal={(toggleModal) =>
                            setModalState({
                                ...JobListDefaultModalState,
                                isWorkersModalOpen: toggleModal,
                            })
                        }
                        title='Active Workers'
                        key='workers-modal'
                    >
                        <table style={{ textAlign: "center", width: "100%" }}>
                            <thead>
                                <tr>
                                    <th>Active Workers</th>
                                </tr>
                            </thead>
                            <tbody>
                                {activeWorkers ? (
                                    Object.keys(activeWorkers).map((workerKey: string) => (
                                        <>
                                            <tr>
                                                <strong>Worker for: {workerKey}</strong>
                                            </tr>
                                            {activeWorkers[workerKey].map(
                                                (worker: string, index: number) => (
                                                    <tr key={worker + index}>
                                                        <td>{worker}</td>
                                                    </tr>
                                                )
                                            )}
                                        </>
                                    ))
                                ) : (
                                    <tr>No workers found</tr>
                                )}
                            </tbody>
                        </table>
                    </Modal>
                )}
                <JobList
                    totalJobs={jobsData.ResultCount.Total}
                    recreateExistingJob={recreateJobFromRow}
                    filteredJobs={jobsData.filteredJobs}
                    sortIndex={jobsData.sortInfo.sortIndex}
                    activeColumn={jobsData.sortInfo.activeColumn}
                    sortByColumn={sortByColumnHeader}
                    deleteJob={deleteJob}
                    archiveJob={archiveJob}
                    handleFilterSubmit={handleJobFilterSubmit}
                    handleFilterRemove={handleRemoveActiveFilter}
                    clearActiveFilters={clearActiveFilters}
                    activeFilters={jobsData.activeFilters}
                    emptyResultsMessage={jobsData.emptyResultsMessage}
                    setJobListPage={setJobListPage}
                    isFilterInProgress={jobsData.isFilterInProgress}
                    activePage={jobsData.activePage}
                ></JobList>
            </div>
        </>
    );
}
